import { FunctionComponent, SyntheticEvent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import styles from './duplicateClosingDay.module.scss'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import Comment from '@mui/icons-material/Comment'
import { Daycare } from '@/types/Daycare.ts'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { Check, Close } from '@mui/icons-material'
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    Chip,
    TextField
} from '@mui/material'
import moment from 'moment/moment'
import { useMutation } from '@apollo/client'
import { DUPLICATE_CLOSING_DAY } from '@/modules/API/daycare/closingDayHandler.ts'
import { ClosingDay } from '@/types/ClosingDays.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useDialog } from '@/hooks/useDialog/useDialog'
import SelectMultipleDateDialogComponent from '@/container/planning/Dialog/occasionalSlot/occasionalSlotForm/components/SelectMultipleDateDialog/selectMultipleDateDialog'
import { DAY_FORMAT } from '@/container/planning/Dialog/occasionalSlot/models/constants'

interface DuplicateClosingDayDialogProps {
    open: boolean
    handleClose: () => void
    closingDay: ClosingDay
    daycare: Daycare | undefined
    refetchAllEvents: () => Promise<void>
}

const DuplicateClosingDayDialog: FunctionComponent<DuplicateClosingDayDialogProps> = ({
    open,
    handleClose,
    daycare,
    closingDay,
    refetchAllEvents
}: DuplicateClosingDayDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [dateList, setDateList] = useState<string[]>([])

    function handleDateRemove(dateToRemove: string): void {
        setDateList(dateList.filter((date: string): boolean => date !== dateToRemove))
    }

    const [duplicateEventDay] = useMutation(DUPLICATE_CLOSING_DAY, {
        variables: {
            daycareId: daycare?.id,
            closingDayId: closingDay.id,
            dateList: dateList
        },
        onCompleted: () => {
            refetchAllEvents()
            showNotificationSuccess(t('closingDayDuplicated'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('closingDayNotDuplicated'))
        }
    })

    const { dialog: SelectMultipleDateDialog, openDialog: openSelectMultipleDateDialog } = useDialog({
        Component: SelectMultipleDateDialogComponent,
        props: () => ({
            daycare,
            dateList,
            setDateList,
            startDate: moment().format(DAY_FORMAT),
            closingDays: []
        }),
        dialogTitle: false
    })

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('duplicateClosingDay')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.TitleContainer}>
                    <Close className={styles.IconDaycare} />
                    <div className={styles.TextContainer}>
                        <p className={styles.Title}>{closingDay.closingDaysReason}</p>
                        <p className={styles.Date}>
                            {t('closingDayOf') +
                                ' ' +
                                format(parse(closingDay.startDate, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                    locale: getLocalFromLanguage()
                                })}
                            {closingDay.startDate !== closingDay.endDate &&
                                ' ' +
                                    t('until').toLowerCase() +
                                    ' ' +
                                    format(parse(closingDay.endDate, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                        locale: getLocalFromLanguage()
                                    })}
                        </p>
                    </div>
                </div>
                {closingDay.closingDaysMessage && (
                    <>
                        <div className={styles.BreakLine}></div>
                        <div className={styles.Container}>
                            <Comment className={styles.IconDaycare} />
                            <p className={styles.Text}>{closingDay.closingDaysMessage}</p>
                        </div>
                    </>
                )}
                <div className={styles.BreakLine}></div>
                <div className={styles.AutocompleteContainer}>
                    <p className={styles.Text}>{t('areYouSureDuplicateClosingDay')}</p>
                    <Autocomplete
                        multiple
                        options={dateList}
                        limitTags={3}
                        value={dateList}
                        onChange={(_event: SyntheticEvent, newValue: string[]) => setDateList(newValue)}
                        renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
                            value.map((option: string, index: number) => (
                                <div key={index}>
                                    <Chip
                                        label={option}
                                        {...getTagProps({ index })}
                                        onDelete={() => handleDateRemove(option)}
                                    />
                                </div>
                            ))
                        }
                        renderInput={(params: AutocompleteRenderInputParams) => (
                            <TextField {...params} variant='outlined' rows={1} multiline={true} label={t('dateList')} />
                        )}
                        className={styles.Autocomplete}
                        onOpen={openSelectMultipleDateDialog}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<Check />}
                        customText={t('yes')}
                        onClick={duplicateEventDay}
                        className={styles.Button}
                        disabled={dateList.length === 0}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<Close />}
                        customText={t('no')}
                        onClick={handleClose}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
            {SelectMultipleDateDialog}
        </Dialog>
    )
}

export default DuplicateClosingDayDialog
